// Copyright 2025, University of Maryland, All Rights Reserved
import { useState } from "react"
import { format } from "date-fns"
import { formatInTimeZone } from "date-fns-tz"
import {
  ChevronDown,
  ChevronUp,
  FileText,
  CloudDownload,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Download
} from "lucide-react"
import type { ComparisonSummary } from "@/api/comparison"

interface ComparisonsTableProps {
  comparisons: ComparisonSummary[]
  onViewDetails: (comparisonId: string) => void
  onDelete: (comparisonId: string) => void
  onDownload: (comparisonId: string) => void
  onDownloadPDF: (comparisonId: string) => void
  isLoading?: boolean
  loadingComparisonId?: string | null
  loadingAction?: 'view' | 'pdf' | null
}

export function ComparisonsTable({
  comparisons,
  onViewDetails,
  onDelete,
  onDownload,
  onDownloadPDF,
  isLoading = false,
  loadingComparisonId = null,
  loadingAction = null
}: ComparisonsTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [sortBy, setSortBy] = useState<'created_at' | 'status' | 'llm_cost'>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  // Toggle row expansion
  const toggleRow = (comparisonId: string) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(comparisonId)) {
      newExpanded.delete(comparisonId)
    } else {
      newExpanded.add(comparisonId)
    }
    setExpandedRows(newExpanded)
  }

  // Sort comparisons
  const sortedComparisons = [...comparisons].sort((a, b) => {
    let aVal: any = a[sortBy]
    let bVal: any = b[sortBy]

    // Handle date sorting
    if (sortBy === 'created_at') {
      aVal = a.created_at ? new Date(a.created_at).getTime() : 0
      bVal = b.created_at ? new Date(b.created_at).getTime() : 0
    }

    // Handle cost sorting
    if (sortBy === 'llm_cost') {
      aVal = parseFloat(a.llm_cost || '0')
      bVal = parseFloat(b.llm_cost || '0')
    }

    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1
    } else {
      return aVal < bVal ? 1 : -1
    }
  })

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />
      case 'processing':
        return <Clock className="h-5 w-5 text-yellow-500 animate-spin" />
      default:
        return <Clock className="h-5 w-5 text-gray-400" />
    }
  }

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'failed':
        return 'bg-red-100 text-red-800'
      case 'processing':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Pagination logic
  const totalComparisons = sortedComparisons.length
  const totalPages = Math.ceil(totalComparisons / pageSize)
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize
  const paginatedComparisons = sortedComparisons.slice(startIndex, endIndex)

  // Handle column sort
  const handleSort = (column: 'created_at' | 'status' | 'llm_cost') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortOrder('desc')
      setCurrentPage(1)
    }
  }

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize)
    setCurrentPage(1)
  }

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1))
  }

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1))
  }

  // Only show full-page loading if it's initial load (not action-specific loading)
  if (isLoading && !loadingComparisonId) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    )
  }

  if (comparisons.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No comparisons found</p>
        <p className="text-sm text-gray-500 mt-2">Create a new comparison to get started</p>
      </div>
    )
  }

  return (
    <>
    {/* Pagination Controls - Top */}
    <div className="flex justify-between items-center mb-4">
      <div className="text-sm text-gray-600">
        Showing {startIndex + 1}-{Math.min(endIndex, totalComparisons)} of {totalComparisons} comparisons
      </div>
      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-600">Rows per page:</label>
        <select
          value={pageSize}
          onChange={(e) => handlePageSizeChange(Number(e.target.value))}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>
    </div>
    <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
      <table className="min-w-full divide-y divide-gray-300">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-3 py-3 text-left w-8">
              <span className="sr-only">Expand/Collapse</span>
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('created_at')}
            >
              <div className="flex items-center gap-1">
                Created
                {sortBy === 'created_at' && (
                  sortOrder === 'desc' ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />
                )}
              </div>
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Documents
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Type
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('status')}
            >
              <div className="flex items-center gap-1">
                Status
                {sortBy === 'status' && (
                  sortOrder === 'desc' ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />
                )}
              </div>
            </th>
            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
              Actions
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Prompt
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created By
            </th>
            <th
              scope="col"
              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
              onClick={() => handleSort('llm_cost')}
            >
              <div className="flex items-center gap-1">
                Cost
                {sortBy === 'llm_cost' && (
                  sortOrder === 'desc' ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />
                )}
              </div>
            </th>
            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Score
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {paginatedComparisons.map((comparison) => (
            <>
              <tr key={comparison.comparison_id} className="hover:bg-gray-50">
                <td className="px-3 py-4 whitespace-nowrap">
                 <button
  onClick={() => toggleRow(comparison.comparison_id)}
  className="text-gray-400 hover:text-gray-600"
  aria-label={expandedRows.has(comparison.comparison_id) ? "Collapse row" : "Expand row"}
>
  {expandedRows.has(comparison.comparison_id) ? (
    <ChevronUp className="h-5 w-5" />
  ) : (
    <ChevronDown className="h-5 w-5" />
  )}
</button>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                  {comparison.created_at ? (() => {
                    let dateStr = comparison.created_at;
                    if (!dateStr.includes('+') && !dateStr.endsWith('Z')) {
                      dateStr = dateStr + 'Z';
                    }
                    return formatInTimeZone(new Date(dateStr), 'America/New_York', 'MMM d, yyyy h:mm a');
                  })() : 'N/A'}
                </td>
                <td className="px-4 py-4 text-sm text-gray-900 max-w-xs">
                  {comparison.documents_compared && comparison.documents_compared.length > 0 ? (
                    <div className="flex flex-col gap-1">
                      {comparison.documents_compared.map((doc, idx) => (
                        <div key={idx} className="truncate text-xs" title={doc}>
                          {doc}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-400">No documents</span>
                  )}
                </td>
                <td className="px-4 py-4 text-sm text-gray-900">
                  <span className="truncate block" title={comparison.comparison_type?.replace('_', ' ') || 'Unknown'}>
                    {comparison.comparison_type?.replace('_', ' ') || 'Unknown'}
                  </span>
                </td>
                <td className="px-3 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(comparison.status)}
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(comparison.status)}`}>
                      {comparison.status}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => onViewDetails(comparison.comparison_id)}
                      className="p-1 hover:bg-blue-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="View Details"
                      disabled={loadingComparisonId === comparison.comparison_id && loadingAction === 'view'}
                    >
                      {loadingComparisonId === comparison.comparison_id && loadingAction === 'view' ? (
                        <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                      ) : (
                        <Eye className="h-4 w-4 text-blue-600" />
                      )}
                    </button>
                    {comparison.status === 'completed' && (
                      <button
                        onClick={() => onDownloadPDF(comparison.comparison_id)}
                        className="p-1 hover:bg-green-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Download PDF"
                        disabled={loadingComparisonId === comparison.comparison_id && loadingAction === 'pdf'}
                      >
                        {loadingComparisonId === comparison.comparison_id && loadingAction === 'pdf' ? (
                          <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full" />
                        ) : (
                          <Download className="h-4 w-4 text-green-600" />
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => onDelete(comparison.comparison_id)}
                      className="p-1 hover:bg-red-100 rounded transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </button>
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-gray-900 max-w-xs">
                  <span className="truncate block" title={comparison.prompt_name || 'N/A'}>
                    {comparison.prompt_name || '-'}
                  </span>
                </td>
                <td className="px-4 py-4 text-sm text-gray-900">
                  <span className="truncate block" title={comparison.created_by || 'Unknown'}>
                    {comparison.created_by || '-'}
                  </span>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                  {comparison.llm_cost ? `$${parseFloat(comparison.llm_cost).toFixed(4)}` : '-'}
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                  {comparison.similarity_score !== undefined ? (
                    <div className="flex items-center gap-2">
                      <span>{(comparison.similarity_score * 100).toFixed(0)}%</span>
                      {comparison.alignment_rating && (
                        <span className="text-xs text-gray-500">({comparison.alignment_rating})</span>
                      )}
                    </div>
                  ) : (
                    '-'
                  )}
                </td>
              </tr>
              {/* Expanded row with additional details */}
              {expandedRows.has(comparison.comparison_id) && (
                <tr key={`${comparison.comparison_id}-expanded`}>
                  <td colSpan={10} className="px-4 py-4 bg-gray-50">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-medium text-gray-700">Comparison ID:</p>
                        <p className="text-gray-600 font-mono text-xs">{comparison.comparison_id}</p>
                      </div>
                      {comparison.documents_compared && comparison.documents_compared.length > 0 && (
                        <div>
                          <p className="font-medium text-gray-700">Documents Compared:</p>
                          <ul className="list-disc list-inside text-gray-600">
                            {comparison.documents_compared.map((doc, idx) => (
                              <li key={idx}>{doc}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {comparison.completed_at && (
                        <div>
                          <p className="font-medium text-gray-700">Completed:</p>
                          <p className="text-gray-600">
                            {(() => {
                              let dateStr = comparison.completed_at;
                              if (!dateStr.includes('+') && !dateStr.endsWith('Z')) {
                                dateStr = dateStr + 'Z';
                              }
                              return formatInTimeZone(new Date(dateStr), 'America/New_York', 'MMM d, yyyy h:mm:ss a');
                            })()}
                          </p>
                        </div>
                      )}
                      {comparison.prompt_name && (
                        <div>
                          <p className="font-medium text-gray-700">Prompt Used:</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-gray-900 font-medium">{comparison.prompt_name}</span>
                            {comparison.analysis_type && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                                {comparison.analysis_type}
                              </span>
                            )}
                            {comparison.prompt_type && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                                {comparison.prompt_type}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      {comparison.result_size_mb && (
                        <div>
                          <p className="font-medium text-gray-700">Result Size:</p>
                          <p className="text-gray-600">{comparison.result_size_mb} MB</p>
                        </div>
                      )}
                      {comparison.message && (
                        <div className="col-span-2">
                          <p className="font-medium text-gray-700">Message:</p>
                          <p className="text-gray-600">{comparison.message}</p>
                        </div>
                      )}
                      {comparison.error && (
                        <div className="col-span-2">
                          <p className="font-medium text-red-700">Error:</p>
                          <p className="text-red-600">{comparison.error}</p>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </>
          ))}
        </tbody>
      </table>
    </div>

    {/* Pagination Controls - Bottom */}
    {totalPages > 1 && (
      <div className="flex justify-between items-center pt-4 border-t mt-4">
        <div className="text-sm text-gray-600">
          Page {currentPage} of {totalPages}
        </div>
        <div className="flex gap-2">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
          >
            ← Previous
          </button>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
          >
            Next →
          </button>
        </div>
      </div>
    )}
    </>
  )
}